import { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { privateKeyToAccount } from 'viem/accounts'
// eslint-disable-next-line no-restricted-imports
import { Config, useAccount, useChainId, useConnect, WagmiProvider } from 'wagmi'

import { getSandboxConfig } from '@/config/wagmi/config.sandbox'
import { useStore } from '@/domain/state'
import { SandboxNetwork } from '@/domain/state/sandbox'
import { router } from '@/routing/router'

const MainWagmiConfigContext = createContext<Config | null>(null)

export function useMainWagmiConfig(): Config | null {
  const config = useContext(MainWagmiConfigContext)
  return config
}

export interface WagmiConfigProviderProps {
  mainConfig: Config
  router: typeof router // router is required purely to handle refreshes on sandbox specific links - should remove in the future
  children: React.ReactNode
}

export function WagmiConfigProvider({ mainConfig, router, children }: WagmiConfigProviderProps) {
  useAutoSwitchToSandbox(router)
  const { network } = useStore((state) => state.sandbox)
  const sandboxConfig = getSandboxConfig(network)

  const activeConfig = useMemo(() => {
    if (sandboxConfig && network?.sandboxEnabled) {
      return sandboxConfig
    }
    return mainConfig
  }, [sandboxConfig, network?.sandboxEnabled, mainConfig])

  return (
    <MainWagmiConfigContext.Provider value={mainConfig}>
      <WagmiProvider config={activeConfig}>
        {network?.sandboxEnabled && network && (
          <>
            <SandboxConnectorGuard network={network} />
            <SandboxInvariant network={network} />
          </>
        )}
        {children}
      </WagmiProvider>
    </MainWagmiConfigContext.Provider>
  )
}

function SandboxConnectorGuard({ network }: { network: SandboxNetwork }) {
  const { address } = useAccount()
  const { connectors, connect } = useConnect()

  const expectedAddress =
    network.ephemeralAccountAddress ??
    (network.ephemeralAccountPrivateKey ? privateKeyToAccount(network.ephemeralAccountPrivateKey).address : undefined)

  useEffect(() => {
    if (!expectedAddress) {
      return
    }
    if (address !== expectedAddress) {
      const mockConnector = connectors.find((c) => c.id === 'mock' || c.name === 'Mock')
      if (mockConnector) {
        connect({ connector: mockConnector })
      }
    }
  }, [address, expectedAddress, connectors, connect])

  return null
}

function SandboxInvariant({ network }: { network: SandboxNetwork }) {
  const chainId = useChainId()

  if (chainId !== network.forkChainId) {
    throw new Error(
      `Sandbox invariant violation: sandboxEnabled=true but chainId=${chainId} !== forkChainId=${network.forkChainId}`,
    )
  }

  return null
}

function useAutoSwitchToSandbox(routerInstance: typeof router): void {
  const hasRunRef = useRef(false)

  if (hasRunRef.current) return
  hasRunRef.current = true

  const { network, setNetwork } = useStore.getState().sandbox
  if (network?.sandboxEnabled || !network) return

  const matches = routerInstance.matchRoutes(routerInstance.state.location.pathname)
  const detailsMatch = matches.find((m) => m.routeId === '/markets/$chainId/$asset')

  if (detailsMatch?.params?.chainId === network.forkChainId) {
    setNetwork((prev) => (prev ? { ...prev, sandboxEnabled: true } : prev))
  }
}
